import { useState, useMemo } from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { AlertCircle, CheckCircle2, Copy, ExternalLink, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { toast } from "sonner";
import { diagnoseGoogleOauthError } from "@/lib/google-oauth-diagnosis";
import { getGoogleOauthConfig } from "@/lib/oauth-diagnosis.functions";

const searchSchema = z.object({
  reason: z.string().optional(),
  oauth: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/oauth-diagnosis")({
  validateSearch: (s) => searchSchema.parse(s),
  component: OauthDiagnosisPage,
});

function OauthDiagnosisPage() {
  const search = useSearch({ from: "/_authenticated/oauth-diagnosis" });
  const [input, setInput] = useState(search.reason ?? "");

  const fetchConfig = useServerFn(getGoogleOauthConfig);
  const { data: cfg } = useQuery({
    queryKey: ["oauth-config"],
    queryFn: () => fetchConfig({}),
  });

  const diag = useMemo(() => {
    const raw = (input || search.reason || "").trim();
    if (!raw) return null;
    return diagnoseGoogleOauthError(raw);
  }, [input, search.reason]);

  const copy = (s: string) => {
    navigator.clipboard.writeText(s);
    toast.success("In Zwischenablage kopiert");
  };

  return (
    <div className="p-8 space-y-6 max-w-4xl">
      <PageHeader
        title="OAuth-Diagnose"
        description="Übersetzt Google-OAuth-Fehler (redirect_uri_mismatch, access_denied, …) in konkrete Ursachen und Schritte."
      />

      <HelpPanel
        title="Hilfe: Google-Login-Fehler verstehen"
        what={
          <>
            Wenn das Verbinden mit Google scheitert (z.B. "redirect_uri_mismatch"), zeigt diese
            Seite die <b>Fehlermeldung in Klartext</b> und sagt dir genau, was zu tun ist.
          </>
        }
        why={
          <>
            Google-Fehler sind kryptisch. Hier siehst du, ob es ein Konfigurations-Fehler ist oder
            du was im Google-Konto klicken musst.
          </>
        }
        how={[
          <>Auf die Fehlermeldung klicken, die du gerade bekommen hast.</>,
          <>Anleitung folgen (z.B. "Redirect-URI in Google Cloud Console hinzufügen").</>,
          <>Erneut verbinden — sollte jetzt klappen.</>,
        ]}
        tips={[<>"access_denied" = du hast "abbrechen" geklickt. Einfach nochmal versuchen.</>]}
        glossary={[
          {
            term: "OAuth",
            def: "Sicheres Login-Verfahren. Du loggst dich bei Google ein und gibst uns Zugriff, ohne uns dein Passwort zu geben.",
          },
          {
            term: "Redirect-URI",
            def: "Adresse, an die Google nach dem Login zurückleitet. Muss bei Google exakt so eingetragen sein.",
          },
        ]}
      />

      {search.oauth === "error" && search.reason && (
        <Card className="p-4 bg-destructive/10 border-destructive/40 flex items-start gap-3">
          <AlertCircle className="size-5 text-destructive shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium text-destructive">
              Fehler vom OAuth-Callback automatisch übernommen
            </div>
            <div className="text-muted-foreground mt-1 break-all">{search.reason}</div>
          </div>
        </Card>
      )}

      <Card className="p-6 bg-card/60 border-border space-y-3">
        <div className="text-sm font-semibold flex items-center gap-2">
          <Search className="size-4" /> Fehlermeldung analysieren
        </div>
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Füge hier den ?reason=… Wert aus der URL, eine Google-Fehlermeldung oder den Server-Fehlertext ein…"
          rows={3}
          className="font-mono text-xs"
        />
        <div className="flex gap-2 flex-wrap">
          {[
            "redirect_uri_mismatch",
            "access_denied",
            "admin_policy_enforced",
            "invalid_client",
            "invalid_scope",
            "no_refresh_token",
            "invalid_state",
            "Token exchange failed: 400 invalid_grant",
          ].map((p) => (
            <Button key={p} variant="outline" size="sm" onClick={() => setInput(p)}>
              {p}
            </Button>
          ))}
        </div>
      </Card>

      {diag && (
        <Card className="p-6 bg-card/60 border-border space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge
                  variant={diag.severity === "error" ? "destructive" : "secondary"}
                  className="uppercase"
                >
                  {diag.severity}
                </Badge>
                <code className="text-xs text-muted-foreground">{diag.code}</code>
              </div>
              <h3 className="text-lg font-semibold">{diag.title}</h3>
            </div>
          </div>

          <div className="text-sm">
            <div className="font-medium mb-1">Wahrscheinliche Ursache</div>
            <p className="text-muted-foreground">{diag.cause}</p>
          </div>

          <div className="text-sm">
            <div className="font-medium mb-2">Schritte zur Behebung</div>
            <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
              {diag.steps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          </div>

          {diag.docsUrl && (
            <a
              href={diag.docsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Google-Dokumentation öffnen <ExternalLink className="size-3" />
            </a>
          )}

          <div className="pt-3 border-t border-border text-xs text-muted-foreground">
            <div className="font-medium mb-1">Roh-Antwort von Google</div>
            <code className="block bg-muted/50 p-2 rounded break-all">{diag.rawReason}</code>
          </div>
        </Card>
      )}

      <Card className="p-6 bg-card/60 border-border space-y-3">
        <div className="text-sm font-semibold">Aktuelle OAuth-Konfiguration</div>
        {!cfg ? (
          <div className="text-sm text-muted-foreground">Lädt…</div>
        ) : (
          <div className="space-y-3 text-sm">
            <ConfigRow label="Redirect-URI (muss in Google Cloud exakt eingetragen sein)">
              <div className="flex gap-2 items-center">
                <Input value={cfg.redirect_uri} readOnly className="font-mono text-xs" />
                <Button size="sm" variant="outline" onClick={() => copy(cfg.redirect_uri)}>
                  <Copy className="size-3" />
                </Button>
              </div>
            </ConfigRow>
            <ConfigRow label="Scope">
              <code className="text-xs">{cfg.scope}</code>
            </ConfigRow>
            <ConfigCheck label="GOOGLE_OAUTH_CLIENT_ID gesetzt" ok={cfg.client_id_present} />
            <ConfigCheck
              label="GOOGLE_OAUTH_CLIENT_SECRET gesetzt"
              ok={cfg.client_secret_present}
            />
            <ConfigCheck
              label="IMAP_ENCRYPTION_KEY gesetzt (≥13 Zeichen)"
              ok={cfg.imap_key_present}
            />
          </div>
        )}
        <div className="pt-2">
          <Link to="/seed-inboxes" className="text-sm text-primary hover:underline">
            ← Zurück zu Test-Postfächern
          </Link>
        </div>
      </Card>
    </div>
  );
}

function ConfigRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  );
}

function ConfigCheck({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? (
        <CheckCircle2 className="size-4 text-emerald-500" />
      ) : (
        <AlertCircle className="size-4 text-destructive" />
      )}
      <span className={ok ? "" : "text-destructive"}>{label}</span>
    </div>
  );
}
